/**
 * CreditTips Component - Credit Building Tips for Dashboard
 * Features:
 * - Curated core tips for all users (especially beginners)
 * - AI personalized tips for users with debt data
 * - Contextual tips based on app behavior if no debt data
 * - Tap to learn more functionality
 */

import { useState, useEffect } from 'react';
import { apiGet } from '@/utils/api';

// Curated credit tips for beginners
const CURATED_TIPS = [
  {
    id: 'tip-1',
    icon: '📅',
    title: 'Pay Bills On Time',
    summary: 'Payment history is 35% of your credit score. Set up autopay!',
    detail: 'Payment history is the most important factor in your credit score. Even one late payment can drop your score significantly. Set up automatic payments for at least the minimum due on all accounts.',
    category: 'payment'
  },
  {
    id: 'tip-2',
    icon: '📊',
    title: 'Keep Utilization Below 30%',
    summary: "Don't use more than 30% of your available credit.",
    detail: 'Credit utilization (how much of your available credit you use) accounts for 30% of your score. If you have a $1,000 limit, try to keep your balance below $300. Under 10% is even better for top scores.',
    category: 'utilization'
  },
  {
    id: 'tip-3',
    icon: '🏦',
    title: "Don't Close Old Cards",
    summary: 'Older accounts help your credit age and available credit.',
    detail: 'The length of your credit history matters. Keep your oldest accounts open, even if you rarely use them. Closing old cards reduces your available credit and shortens your credit age.',
    category: 'history'
  },
  {
    id: 'tip-4',
    icon: '🔍',
    title: 'Check Your Credit Report',
    summary: 'Get free reports at AnnualCreditReport.com. Look for errors!',
    detail: "You're entitled to one free credit report from each bureau annually. Review them for errors - disputes can be filed online. Incorrect late payments or accounts that aren't yours can hurt your score.",
    category: 'monitoring'
  },
  {
    id: 'tip-5',
    icon: '🎯',
    title: 'Limit Hard Inquiries',
    summary: 'Too many credit applications hurt your score.',
    detail: 'Each time you apply for credit, a hard inquiry appears on your report. Multiple inquiries in a short period can lower your score. Rate shopping for mortgages or auto loans within 14-45 days counts as one inquiry.',
    category: 'inquiries'
  },
  {
    id: 'tip-6',
    icon: '💳',
    title: 'Become an Authorized User',
    summary: "Piggyback on someone else's good credit history.",
    detail: "Ask a family member with excellent credit to add you as an authorized user on their oldest card. You'll inherit their account history. You don't even need to use the card - just benefit from their good habits.",
    category: 'building'
  },
  {
    id: 'tip-7',
    icon: '🏗️',
    title: 'Consider a Secured Card',
    summary: 'Build credit with a deposit-backed card.',
    detail: "Secured credit cards require a deposit (usually $200-500) which becomes your credit limit. Use it for small purchases, pay in full each month, and in 6-12 months you'll build credit. Many upgrade to unsecured cards.",
    category: 'building'
  },
  {
    id: 'tip-8',
    icon: '⚡',
    title: 'Pay Twice a Month',
    summary: 'Reduce your utilization by paying before the statement closes.',
    detail: "Credit card companies report your balance on the statement date. If you pay down your balance before this date, you'll show lower utilization. Some people pay every paycheck to keep utilization minimal.",
    category: 'utilization'
  }
];

// Tip Detail Modal
function TipDetailModal({ tip, onClose }) {
  if (!tip) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 animate-fade-in">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center text-2xl">
            {tip.icon}
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900">{tip.title}</h3>
            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full capitalize">
              {tip.category}
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl"
          >
            ✕
          </button>
        </div>
        
        <p className="text-gray-700 leading-relaxed mb-6">
          {tip.detail}
        </p>
        
        <button
          onClick={onClose}
          className="w-full py-3 bg-purple-600 text-white rounded-xl font-medium hover:bg-purple-700 transition-colors"
        >
          Got it!
        </button>
      </div>
    </div>
  );
}

// Main Component
export default function CreditTips({ isPremium = false, className = '' }) {
  const [tips, setTips] = useState([]);
  const [personalizedTips, setPersonalizedTips] = useState([]);
  const [selectedTip, setSelectedTip] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasDebtData, setHasDebtData] = useState(false);

  useEffect(() => {
    loadTips();
  }, [isPremium]);

  const loadTips = async () => {
    setLoading(true);
    try {
      // Check if user has debt data for personalized tips
      const debtResponse = await apiGet('/debt');
      const hasDebts = debtResponse.ok && (await debtResponse.json()).length > 0;
      setHasDebtData(hasDebts);

      if (isPremium && hasDebts) {
        // Load AI personalized tips for premium users with debt data
        const aiResponse = await apiGet('/credit-tips/personalized');
        if (aiResponse.ok) {
          const aiTips = await aiResponse.json();
          setPersonalizedTips(aiTips.tips || []);
        }
      }

      // Always show curated tips (select 2-3 random ones)
      const shuffled = [...CURATED_TIPS].sort(() => 0.5 - Math.random());
      setTips(shuffled.slice(0, 3));

    } catch (error) {
      console.error('Error loading credit tips:', error);
      // Fallback to curated tips only
      setTips(CURATED_TIPS.slice(0, 3));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className={`animate-pulse ${className}`}>
        <div className="h-32 bg-gray-200 rounded-xl"></div>
      </div>
    );
  }

  // Combine personalized and curated tips
  const displayTips = isPremium && personalizedTips.length > 0
    ? [...personalizedTips.slice(0, 2), ...tips.slice(0, 1)]
    : tips;

  return (
    <div className={className}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="text-xl">💳</span>
          <h2 className="text-lg font-bold text-gray-900">Credit Building Tips</h2>
          {isPremium && (
            <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full font-medium">
              Personalized
            </span>
          )}
        </div>
      </div>

      {/* Tips List */}
      <div className="space-y-3">
        {displayTips.map((tip, index) => (
          <button
            key={tip.id || index}
            onClick={() => setSelectedTip(tip)}
            className="w-full p-4 bg-gradient-to-r from-purple-50 to-indigo-50 hover:from-purple-100 hover:to-indigo-100 rounded-xl border border-purple-200 text-left transition-all group"
            data-testid={`credit-tip-${tip.id || index}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center text-xl shadow-sm">
                {tip.icon}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-gray-900 group-hover:text-purple-700 transition-colors">
                  {tip.title}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2">
                  {tip.summary}
                </p>
              </div>
              <div className="text-purple-400 group-hover:text-purple-600 transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
            
            {/* Personalized indicator */}
            {tip.isPersonalized && (
              <div className="mt-2 flex items-center gap-1 text-xs text-purple-600">
                <span>✨</span>
                <span>Based on your debt profile</span>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* Upgrade prompt for free users */}
      {!isPremium && (
        <div className="mt-4 p-3 bg-gradient-to-r from-yellow-50 to-amber-50 rounded-xl border border-yellow-200">
          <div className="flex items-center gap-3">
            <span className="text-2xl">✨</span>
            <div className="flex-1">
              <p className="text-sm font-medium text-yellow-900">
                Upgrade for personalized credit advice
              </p>
              <p className="text-xs text-yellow-700">
                Get AI-powered tips based on your specific debt situation
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tip Detail Modal */}
      <TipDetailModal
        tip={selectedTip}
        onClose={() => setSelectedTip(null)}
      />

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
